import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react'
import { userAPI } from '@/lib/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

type Status = 'processing' | 'complete' | 'error'

const CheckoutSuccessPage = () => {
  const navigate = useNavigate()
  const [status, setStatus] = useState<Status>('processing')
  const [isNewUser, setIsNewUser] = useState(false)

  useEffect(() => {
    let attempts = 0
    const maxAttempts = 15 // Try for 30 seconds (15 * 2s)

    // Check if user has auth token
    const hasAuth = localStorage.getItem('auth_token')

    // If no auth token, this is definitely a new user - show success immediately
    if (!hasAuth) {
      setIsNewUser(true)
      setStatus('complete')
      return
    }

    // For existing users with auth token, poll to verify upgrade
    const checkStatus = setInterval(async () => {
      attempts++

      try {
        // Try to get user profile
        const response = await userAPI.getUserWithTier()

        if (response.data) {
          // Account ready!
          setStatus('complete')
          clearInterval(checkStatus)

          // Existing user upgraded - redirect to dashboard
          setTimeout(() => {
            navigate('/dashboard')
          }, 2000)
        }
      } catch {
        if (attempts >= maxAttempts) {
          setStatus('error')
          clearInterval(checkStatus)
        }
        // Otherwise keep polling
      }
    }, 2000)

    return () => clearInterval(checkStatus)
  }, [navigate])

  if (status === 'processing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <Loader2 className="h-16 w-16 animate-spin text-primary" />
            </div>
            <CardTitle className="text-2xl">Processing Payment...</CardTitle>
            <CardDescription>
              We're setting up your account. This will only take a moment.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>✓ Payment received</p>
              <p>⏳ Creating your account...</p>
              <p className="text-xs mt-4">Please don't close this window</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'complete' && isNewUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md border-green-500">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-600">Payment Successful!</CardTitle>
            <CardDescription className="text-base">Your account has been created</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
              <div className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">
                    Check Your Email
                  </h4>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    We've sent your temporary password to your email address. Use it to log in and
                    then change your password in settings.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Button className="w-full" size="lg" onClick={() => navigate('/auth?mode=login')}>
                Go to Login
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                Didn't receive the email? Check your spam folder
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'complete' && !isNewUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md border-green-500">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-600">Upgrade Successful!</CardTitle>
            <CardDescription>Redirecting to your dashboard...</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
          </CardContent>
        </Card>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md border-yellow-500">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <AlertCircle className="h-16 w-16 text-yellow-500" />
            </div>
            <CardTitle className="text-xl">Upgrade Processing</CardTitle>
            <CardDescription>Your payment was successful!</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-lg text-sm">
              <p className="mb-2">
                Your subscription upgrade is being processed. This usually takes just a moment.
              </p>
              <p className="text-muted-foreground">
                Try refreshing your dashboard, or wait a minute and log in again to see your updated
                subscription.
              </p>
            </div>

            <div className="space-y-2">
              <Button className="w-full" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
              <Button className="w-full" variant="outline" onClick={() => window.location.reload()}>
                Refresh Page
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return null
}

export default CheckoutSuccessPage
